import { FC, ReactNode } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@radix-ui/react-dialog';
import classNames from 'classnames';

import { icon_close_medium } from '@gn-audio/icons';
import {
  Button,
  JabraIcon,
  JabraThemeProvider,
  useFallbackTranslation,
  useJabraTheme,
} from '@gn-audio/jss-components';

import { IJabraTheme } from 'common/jabraTheme/JabraTheme.models';

import styles from './Drawer.module.scss';

export interface DrawerProps {
  children?: ReactNode;
  open: boolean;
  theme?: IJabraTheme;
  size?: 's' | 'md' | 'lg';
  close: () => void;
  title?: string;
  description?: string;
  dialogContentClassName?: string;
}

const Drawer: FC<DrawerProps> = ({
  children,
  open,
  close,
  size = 's',
  dialogContentClassName,
  theme,
  title,
  description,
}) => {
  const { t } = useFallbackTranslation();

  const closeDrawer = () => {
    close();
  };

  let sizeClasses;

  if (size === 's') {
    sizeClasses = 'w-[90%]  py-12 md:w-[622px] xl:w-[485px]';
  }
  if (size === 'md') {
    sizeClasses = 'w-[30vw]';
  }
  if (size === 'lg') {
    sizeClasses = 'w-[50vw]';
  }

  const contextTheme = useJabraTheme();

  const {
    foregroundColor = undefined,
    backgroundColor = undefined,
    darkTheme = false,
  } = contextTheme ?? theme ?? {};

  const themeVariables = {} as any;
  if (foregroundColor) {
    themeVariables['--fgColor'] = foregroundColor;
  }
  if (backgroundColor) {
    themeVariables['--bgColor'] = backgroundColor;
  }

  return (
    <Dialog open={open}>
      <DialogPortal>
        <DialogOverlay
          className="fixed inset-0 z-30 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onClick={closeDrawer}
        >
          <DialogContent
            className={classNames(
              'absolute right-0 top-0 z-30 h-screen overflow-y-scroll rounded-l-[1.5rem] py-12 max-md:w-[90%] md:min-w-[40rem]',
              'bg-theme text-theme',
              darkTheme ? 'dark' : 'light',
              styles.dialogContent,
              sizeClasses,
              dialogContentClassName
            )}
            style={themeVariables}
            onEscapeKeyDown={closeDrawer}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {title && <DialogTitle className="hidden">{title}</DialogTitle>}
            {description && <DialogDescription className="hidden">{description}</DialogDescription>}
            <JabraThemeProvider theme={theme}>
              <DialogClose asChild>
                <Button
                  className="ml-auto mr-6 flex md:mr-12"
                  aria-label={t('system.Close')}
                  variant="tertiary"
                  size="l"
                  icon={<JabraIcon Icon={icon_close_medium} />}
                  onClick={closeDrawer}
                  data-testid="drawer-close-button"
                />
              </DialogClose>
              {children}
            </JabraThemeProvider>
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    </Dialog>
  );
};

export default Drawer;
